import axios from "axios";
import { registerResponseInterceptor } from "@/api/axiosInterceptor";
import { paramsSerializer } from "@/api/v4.request";

const axiosInstance = axios.create({
  baseURL: `/gooduser/api/v4`,
  paramsSerializer,
});

registerResponseInterceptor(axiosInstance);

/**
 * @param {PageParam} pageParam
 * @return {{size?: number, lastId?: string|number, sort?: string[]}|{size?: number, page?: number, sort?: string[]}}
 */
function convertPageParamToParams(pageParam) {
  return pageParam.lastId
    ? {
        lastId: pageParam.lastId,
        size: pageParam.size,
        sort: pageParam.sort
          ? convertSortParamsToParam(pageParam.sort)
          : undefined,
      }
    : {
        page: pageParam.page,
        size: pageParam.size,
        sort: pageParam.sort
          ? convertSortParamsToParam(pageParam.sort)
          : undefined,
      };
}

/**
 *
 * @param {SortParam[]} sortParams
 * @return {string[]}
 */
function convertSortParamsToParam(sortParams) {
  return sortParams.map(
    (sortParam) => `${sortParam.field},${sortParam.direction}`
  );
}

/**
 * fetching promotion banners
 * @function
 * @return {Promise<AxiosResponse<OasResponse<GetPromotionBannersResponseDto[]>>>}
 */
export async function fetchPromotionBanners() {
  const path = `/promotion-banners`;

  return await axiosInstance.get(path);
}

/**
 * fetching promotion hot tags
 * @function
 * @return {Promise<AxiosResponse<OasResponse<UserMemberUserApiDto|null>>>}
 */
export async function fetchSessionUser() {
  const path = `/session-user`;

  return await axiosInstance.get(path);
}

/**
 * fetching promotion hot tags
 * @function
 * @return {Promise<AxiosResponse<OasResponse<GetPromotionHotTagsResponseDto[]>>>}
 */
export async function fetchPromotionHotTags() {
  const path = `/promotion-hot-tags`;

  return await axiosInstance.get(path);
}

/**
 * fetching brands
 * @function
 * @param {string?} q
 * @param {PageParam?} pageParam
 * @return {Promise<AxiosResponse<OasSearchResponse<BrandListItemDto[]>>>}
 */
export async function fetchBrands({ q, pageParam } = {}) {
  const path = `/brands`;

  return await axiosInstance.get(path, {
    params: { q, ...convertPageParamToParams(pageParam || {}) },
  });
}

/**
 * 카트 항목 수량 변경
 * @param {number} memberId
 * @param {number} bagId
 * @param {number} count
 * @return {Promise<void>}
 */
export async function updateBagItemCount(memberId, bagId, count) {
  const path = `/members/${memberId}/bags/${bagId}/update-count`;

  return await axiosInstance.post(path, count, {
    headers: {
      "Content-Type": "application/json",
    },
    data: count,
  });
}

/**
 * 카트 항목 번들링
 * @param {number} memberId
 * @param {number[]} bagIds
 * @return {Promise<void>}
 */
export async function bundleBagItems(memberId, bagIds) {
  const path = `/members/${memberId}/bags/bundle-temp`;

  return await axiosInstance.post(path, {
    bagIds,
  });
}

/**
 * 상품 상세 조회
 * @param id
 * @return {Promise<AxiosResponse<OasResponse<ProductDetailsResponseDto>>>}
 */
export async function fetchProductDetails(id) {
  const path = `/products/${id}`;

  return await axiosInstance.get(path);
}

/**
 * 사용자 리뷰 목록 조회
 * @param {number} memberId
 * @param {boolean} isWritten
 * @param {number} perPage
 * @param {number} page
 * @return {Promise<AxiosResponse<OasSearchResponse<ProductReviewResponseDto[]>>>}
 */
export async function fetchMemberProductReviewsPage(
  memberId,
  isWritten,
  { perPage = 10, page = 1 } = { page: 1, perPage: 10 }
) {
  const path = `/members/${memberId}/reviews`;

  return await axiosInstance.get(path, {
    params: {
      isWritten,
      page,
      size: perPage,
    },
  });
}

/**
 * 사용자 재입고알림 목록 조회
 * @param {number} memberId
 * @param {DatePeriod} period
 * @param {number} perPage
 * @param {number} page
 * @return {Promise<AxiosResponse<OasSearchResponse<ProductRestockNotificationResponseDto[]>>>}
 */
export async function fetchProductRestockNotificationsPage(
  memberId,
  period,
  { perPage = 10, page = 1 } = { page: 1, perPage: 10 }
) {
  const path = `/members/${memberId}/product-restock-notifications`;

  return await axiosInstance.get(path, {
    params: {
      page,
      form: !period.from || !period.to ? undefined : period.from,
      to: !period.from || !period.to ? undefined : period.to,
      size: perPage,
    },
  });
}

/**
 * 상품 목록 조회
 * @param {PageParam?} pageParam
 * @param {1|2?} group 카테고리 그룹. (1: 골프, 2: 생활용품)
 * @param {number[]?} brandIds 브랜드 아이디
 * @param {number[]?} categoryIds 카테고리 아이디
 * @param {number[]?} filterIds 필터 아이디
 * @param {"M"|"F"?} genderType 성별코드 (M: 남성용, F: 여성용)
 * @param {"PRODUCT_NAME" | "PRODUCT_CODE"} searchType 검색 타입 (PRODUCT_CODE: 상품코드, PRODUCT_NAME: 상품명 (기본값 -> 빈값 또는 파라미터 없는 경우)
 * @param {string?} q 검색어
 * @param {"recent"|"price"|"star"|"reivew"|"hot"?} sortBy 정렬
 * @return {Promise<axios.AxiosResponse<OasSearchResponse<ProductListItemResponseDto[]>>>}
 */
export async function fetchProducts(
  {
    group,
    brandIds,
    categoryIds,
    filterIds,
    genderType,
    searchType,
    q,
    sortBy = "recent",
    pageParam,
  } = { sortBy: "recent" }
) {
  const path = `/products`;

  return await axiosInstance.get(path, {
    params: {
      group,
      brandIds,
      categoryIds,
      filterIds,
      genderType,
      searchType,
      q,
      sortBy,
      ...convertPageParamToParams(pageParam || {}),
    },
  });
}

/**
 * 생활용품 목록 조회
 * @param {PageParam?} pageParam
 * @return {Promise<axios.AxiosResponse<OasSearchResponse<ProductListItemResponseDto[]>>>}
 */
export async function fetchLivingProducts({ pageParam }) {
  const path = `/living-products`;

  return await axiosInstance.get(path, {
    params: {
      ...convertPageParamToParams(pageParam || {}),
    },
  });
}

/**
 * fetching brand filters
 * @function
 * @param {number?} productCategoryGroup
 * @param {number?} productCategoryId
 * @param {string?} genderType
 * @return {Promise<AxiosResponse<OasResponse<BrandFilterListItemDto[]>>>}
 */
export async function fetchBrandFilters({
  productCategoryGroup,
  productCategoryId,
  genderType,
} = {}) {
  const path = `/brand-filters`;

  return await axiosInstance.get(path, {
    params: {
      productCategoryGroup,
      productCategoryId,
      genderType,
    },
  });
}

/**
 * 주문 교환 요청
 * @param memberId 사용자 아이디
 * @param orderId 주문 아이디
 * @param orderItemId 주문 항목 아이디
 * @param reason 사유
 * @return {Promise<axios.AxiosResponse<OasResponse<void>>>}
 */
export async function requestExchangeOrderItem({
  memberId,
  orderId,
  orderItemId,
  reason,
}) {
  const path = `/members/${memberId}/orders/${orderId}/items/${orderItemId}/request-exchange`;

  return await axiosInstance.post(path, { reason });
}

/**
 * 주문 반품 요청
 * @param memberId 사용자 아이디
 * @param orderId 주문 아이디
 * @param orderItemId 주문 항목 아이디
 * @param reason 사유
 * @return {Promise<axios.AxiosResponse<OasResponse<void>>>}
 */
export async function requestReturnOrderItem({
  memberId,
  orderId,
  orderItemId,
  reason,
}) {
  const path = `/members/${memberId}/orders/${orderId}/items/${orderItemId}/request-return`;

  return await axiosInstance.post(path, { reason });
}

/**
 * 주문 취소 요청
 * @param memberId 사용자 아이디
 * @param orderId 주문 아이디
 * @param orderItemId 주문 항목 아이디
 * @return {Promise<axios.AxiosResponse<OasResponse<void>>>}
 */
export async function confirmOrderItem({ memberId, orderId, orderItemId }) {
  const path = `/members/${memberId}/orders/${orderId}/items/${orderItemId}/confirm`;

  return await axiosInstance.post(path);
}

/**
 * fetching promotion events
 * @function
 * @return {Promise<AxiosResponse<OasResponse<GetPromotionEventsResponseDto[]>>>}
 */
export async function fetchPromotionEvents() {
  const path = `/promotion-events`;

  return await axiosInstance.get(path);
}

/**
 fetching favorites
 * @param memberId
 * @param {PageParam?} pageParam
 * @return {Promise<AxiosResponse<OasSearchResponse<GetPromotionEventsResponseDto[]>>>}
 */
export async function fetchFavorites({ memberId, pageParam }) {
  const path = `/members/${memberId}/favorites`;

  return await axiosInstance.get(path, {
    params: {
      ...convertPageParamToParams(pageParam || {}),
    },
  });
}

/**
 * fetching total favorites
 * @function
 * @return {Promise<AxiosResponse<OasResponse<number>>>}
 */
export async function fetchTotalFavorites({ memberId }) {
  const path = `/members/${memberId}/total-favorites`;

  return await axiosInstance.get(path);
}

/**
 * 출석체크 이벤트 조회
 * @param memberId
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export async function fetchAttendance({ memberId }) {
  const path = `/attendance`;

  return await axiosInstance.get(path, {
    params: {
      memberId,
    },
  });
}

/**
 * 출석체크 확인 API
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export async function applyAttendance() {
  const path = `/attendance`;

  return await axiosInstance.post(path);
}

/**
 * @typedef DatePeriod
 * @property {string} from
 * @property {string} to
 */

/**
 * @typedef SortParam
 * @property {string} field
 * @property {"asc"|"desc"} direction
 */

/**
 * @typedef PageParam
 * @property {number?} page
 * @property {SortParam[]?} sort
 * @property {number?} size
 */

/**
 * @typedef PageParamByPageNumber
 * @property {number} page
 */

/**
 * @template Data
 * @typedef OasResponse<Data>
 * @property {string} status
 * @property {Data} data
 * @property {string} message
 */

/**
 * @template Data
 * @typedef OasSearchResponse<Data>
 * @property {string} status
 * @property {Data} data
 * @property {string} message
 * @property {number} total
 * @property {boolean} hasNext
 */

/**
 * @typedef UploadFileJson
 * @property {number} id
 * @property {string} uuid
 * @property {string} uploadName
 * @property {string} originalName
 * @property {string} extension
 * @property {string} path
 * @property {string} realUrl
 * @property {number} size
 */

/**
 * @typedef UserMemberUserApiDto
 * @property {number} id
 * @property {string} name
 * @property {string} custNo
 * @property {string} telephone
 * @property {string} email
 * @property {string} gender
 * @property {string} grade
 * @property {string} recommendUsername
 */

/**
 * @typedef GetPromotionHotTagsResponseDto
 * @property {number} id
 * @property {string} name
 * @property {string} startDate
 * @property {string} endDate
 */

/**
 * @typedef GetPromotionBannersResponseDto
 * @property {number} id
 * @property {string} code
 * @property {number} order
 * @property {string} name
 * @property {string} link
 * @property {string} linkType
 * @property {string} viewType
 * @property {string} pcImage
 * @property {UploadFileJson} pcImageJson
 * @property {string} mobileImage
 * @property {UploadFileJson} mobileImageJson
 */

/**
 * @typedef BrandListItemDto
 * @property {number} id
 * @property {string} name
 * @property {string} introduceRawHtml
 * @property {boolean} showMain
 * @property {string} logoImageUrl
 * @property {string} pcImageUrl
 * @property {string} mobileImageUrl
 * @property {number} favorites
 */

/**
 * @typedef ProductDetailsResponseDto
 * @property {number} id
 * @property {string} name
 * @property {string} textName
 * @property {string} detail
 * @property {boolean} isVip
 * @property {boolean} isNormal
 * @property {number} price
 * @property {string} salePercent
 * @property {number} sellingPrice
 * @property {number} shippingPrice
 * @property {string} manufacturer
 * @property {string} shippingService
 * @property {number} reservationMinWeek
 * @property {number} reservationMaxWeek
 * @property {string} shippingInformation
 * @property {string} returnInformation
 * @property {string} informationReturn
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {ProductInformationItem[]} productInformationItems
 * @property {ShippingPickupOption|undefined} shippingPickupOption
 * @property {boolean} isPreorderProduct
 * @property {boolean} usePresent
 * @property {number} maxQuantityPerPerson
 * @property {boolean} isShippingFree
 * @property {number} stock
 * @property {boolean} isBest
 * @property {boolean} useStock
 * @property {boolean} useOption
 * @property {boolean} isKakaoChannelOpen
 * @property {string} mainImageUrl
 * @property {string[]} imageUrls
 * @property {BrandDetailsResponseDto} brand
 * @property {PartnerDetailsResponseDto} partner
 * @property {ProductCategoryDetailsResponseDto} category
 * @property {ProductOptionDetailsResponseDto|undefined} option
 * @property {AvailableCouponInfo[]} availableCoupons
 */

/**
 * @typedef ProductInformationItem
 * @property {string} title
 * @property {string} content
 */

/**
 * @typedef BrandDetailsResponseDto
 * @property {number} id
 * @property {string} name
 * @property {UploadFileJson} logoJson
 * @property {UploadFileJson} pcImageJson
 * @property {UploadFileJson} mobileImageJson
 * @property {string} content
 * @property {boolean} isMainView
 */

/**
 * @typedef PartnerDetailsResponseDto
 * @property {number} id
 * @property {string} name
 * @property {string} businessNumber
 * @property {string} code
 * @property {string} kakaotalkChannelUrl
 * @property {string} businessPhoneNo
 * @property {string} consultationStr
 * @property {string} mailOrderNumber
 * @property {string} representative
 * @property {string} businessLocation
 * @property {string} representativeEmail
 */

/**
 * @typedef ProductCategoryDetailsResponseDto
 * @property {number} id
 * @property {number} parentId
 * @property {string} name
 * @property {number} fee
 * @property {number} orderNo
 * @property {number} groups
 * @property {number} depth
 * @property {string} reviewPlaceholder
 */

/**
 * @typedef ProductOptionDetailsResponseDto
 * @property {number} id
 * @property {string} name
 * @property {string} data
 * @property {string} lastOptionIndex
 * @property {boolean} isCombine
 */

/**
 * @typedef ShippingPickupOption
 * @property {ShippingPickupOptionGolfValue[]} golf
 * @property {number|undefined} date
 */

/**
 * @typedef {"01"|"02"|"03"|"04"|"05"} ShippingPickupOptionGolfValue
 */

/**
 * @typedef ProductReviewResponseDto
 * @property {number} id
 * @property {number} starPoint
 * @property {ProductReviewCategorySelector[]} selectors
 * @property {string} choose
 * @property {string} reviewDetail
 * @property {UploadFileJson} userImage1UploadFileJson
 * @property {UploadFileJson} userImage2UploadFileJson
 * @property {UploadFileJson} userImage3UploadFileJson
 * @property {string} shippingCompleteDay
 * @property {number} hit
 * @property {ProductReviewReplyResponseDto} reply
 * @property {ProductDetailsResponseDto} product
 */

/**
 * @typedef ProductReviewReplyResponseDto
 * @property {number} id
 * @property {string} content
 * @property {string} replierName
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef ProductReviewCategorySelector
 * @property {number} id
 * @property {string} value
 * @property {ProductReviewCategorySelectorItem[]} items
 */

/**
 * @typedef ProductReviewCategorySelectorItem
 * @typedef {number} id
 * @typedef {string} optionName
 * @typedef {number} parentId
 */

/**
 * @typedef ProductRestockNotificationResponseDto
 * @property {number} id
 * @property {number} productOptionId
 * @property {string} optionDataKey
 * @property {string} optionDataName
 * @property {string} optionData
 * @property {string} phone
 * @property {string} status
 * @property {string} sendDate
 * @property {ProductDetailsResponseDto} product
 */

/**
 * @typedef ProductListItemResponseDto
 * @property {number} id
 * @property {number} brandId
 * @property {string} mainImageUrl
 * @property {string} name
 * @property {string} textName
 * @property {boolean} isVip
 * @property {boolean} isNormal
 * @property {number} price
 * @property {string} salePercent
 * @property {number} sellingPrice
 * @property {number} reviewStarPoint
 * @property {number} reviewCount
 * @property {string} manufacturer
 * @property {boolean} isShippingFree
 * @property {boolean} isPreorderProduct
 * @property {boolean} usePresent
 * @property {boolean} isNew
 * @property {boolean} isBest
 * @property {boolean} isEvent
 * @property {boolean} isSoldOut
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef BrandFilterListItemDto
 * @property {number} id
 * @property {string} name
 */

/**
 * @typedef GetPromotionEventsResponseDto
 * @property {number} id
 * @property {string} eventCode
 * @property {string} name
 * @property {string} content
 * @property {string} startDate
 * @property {string} endDate
 * @property {string} pcImage
 * @property {UploadFileJson} pcImageJson
 * @property {string} mobileImage
 * @property {UploadFileJson} mobileImageJson
 * @property {number} productCategoryId
 * @property {string} productJson
 * @property {string} productIds
 * @property {string} type
 * @property {number} sort
 */

/**
 * @typedef GetFavoritesResponseDto
 * @property {number} id
 * @property {string} type
 * @property {ProductListItemResponseDto} product
 */

/**
 * @typedef AvailableCouponInfo
 * @property {number} productCouponId
 * @property {number} eventProductId
 * @property {string} couponDisplayNm
 */
